import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import confirm from "@mnjsplatform/components/lib/common/ConfirmFn";
import {
    Grid,
    TableBandHeader,
    TableHeaderRow,
    VirtualTable,
    TableFixedColumns,
    TableColumnVisibility,
    TableFilterRow
} from "@devexpress/dx-react-grid-bootstrap4";
import {
    FilteringState,
} from '@devexpress/dx-react-grid';
import {
    BandCell,
    FilterCellContent,
    FixedCell,
    HeaderCell,
    VirtualCell
} from "./ARGridCellStyling";
import {ARGridCellExtensions} from "./ARGridCellExtentions";
import {ARGridContextMenu} from "./ARGridContextMenu";
import {useDispatch, useSelector} from "react-redux";
import {GetPageProps, GetRows, ParseFieldsInfo} from "../DataConnector/DataConnector";
import {initSubgrid, unmountSubgrid} from "@mnjsplatform/data/lib/actions/subgridActions";
import {ARDetails} from "./ARDetails";
import { ARFilters } from "./ARFilters";
import classes from '../AnnualRemuneration.module.css'
import { ExpandLink } from "./ExpandLink";
import { FixButton } from "./FixButton";
import { ScreenFormatContext } from "@mnjsplatform/data";
import ARGridFontSize, { getArGridFontSize, setArGridFontSize } from "./ARGridFontSize";
import { ARSearchRow } from "./ARSearchRow";
import i18next from "i18next";
import PlaceholderWrapper from "../../Common/PlaceholderWrapper";

const Notation = () => {

    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    }

    const NotationItem = ({iconClass, description}) => {
        return (
            <div className={`${!isOpen && 'd-inline-block ' || 'd-flex'} ml-1 align-middle`}>
                <i className={"d-inline-block " + iconClass}
                   style={{backgroundSize: "18px", margin: "3px 0 0 0"}}/>{isOpen &&
                <div className={'ml-1'}>{description}</div>}
            </div>
        );
    }

    return (
        <>
            <div className={"d-inline-block align-middle"}>
                <ExpandLink isOpen={isOpen} title={i18next.t("Designations")} clickHandler={toggleOpen}
                            className={"d-inline-block"}/>
                <NotationItem iconClass={classes.attentionIcon} description={i18next.t("ARGrid_1")}/>
                <NotationItem iconClass={classes.tickCircleIcon} description={i18next.t("ARGrid_2")}/>
                <NotationItem iconClass={classes.circleIcon} description={"ARGrid_3"}/>
            </div>
        </>
    );
}

const ARGridComponent = ({rows, rowTreeInfo, columns, columnBands, columnNames, frozenColumnNames, hiddenColumnsInfo, columnStyles, toggleExpand, toggleExpandLevel, pageProps, applyChanges, fixYB, onChangeTabNumber, onChangeFilters, renderReport, tabNumber}) => {
    const hiddenColumnNames = hiddenColumnsInfo.filter(c => c.IsYes === 0).map(c => c.ComboSetId);

    const [gridHiddenColumns, setGridHiddenColumns] = useState(hiddenColumnNames);
    const [gridHeight, setGridHeight] = useState(null);
    const [fontSize, setFontSize] = useState(getArGridFontSize());

    const [contextMenuProps, setContextMenuProps] = useState({top: null, left: null, row: null, isOpen: false});
    const [detailsPopupVisability, setDetailsPopupVisability] = useState({popupType: null, rowKey: null, name: null, orgStructId: null, access: null});

    const [highlightRows, setHighlightRows] = useState([]);

    const headerContentRef = useRef(null);
    const footerContentRef = useRef(null);

    const toggleHiddenColumns = useCallback((columnName) => {
        const indexOfColumn = gridHiddenColumns.indexOf(columnName);
        const arr = [...gridHiddenColumns];
        if (indexOfColumn > -1){
            arr.splice(indexOfColumn, 1);
        } else {
            arr.push(columnName);
        }
        setGridHiddenColumns(arr);
    }, [gridHiddenColumns]);

    const contextMenuPropsHandler = (e, row) => {
        if (row && row.OrgStructTypeIntId === 4 && row.access.Show_ContextMenu) {
            e.preventDefault();
            const {height, x, y} = e.target.getBoundingClientRect();
            setContextMenuProps({top: y + height, left: x, row, isOpen: true});
        }
    }

    const contextMenuClose = () => {
        setContextMenuProps({isOpen: false});
    }

    const displayDetails = (itemType, rowKey, name, orgStructId, access) => {
        setDetailsPopupVisability({popupType: itemType, rowKey: rowKey, name: name, orgStructId, access});
    }

    const detailsPopupClose = () => {
        setDetailsPopupVisability({popupType: null, rowKey: null, name: null, orgStructId: null, access: null});
    }

    const {popupType, rowKey, name, orgStructId, access} = detailsPopupVisability;

    const updateGridHeight = () => {
        let headerHeight = 0;
        let footerHeight = 0;

        if (headerContentRef && headerContentRef.current) {
            const props = headerContentRef.current.getBoundingClientRect();
            headerHeight = props.bottom + 20;
        }

        if (footerContentRef && footerContentRef.current) {
            const props = footerContentRef.current.getBoundingClientRect();
            footerHeight = props.height + 21;
        }

        const height = window.innerHeight - headerHeight - footerHeight;
        setGridHeight(height);
    }

    const changeFontSize = (size) => {
        if (size > 0) {
            setArGridFontSize(size);
            setFontSize(size);
        }
    }

    useEffect(() => {
        if (headerContentRef?.current && footerContentRef?.current) {
            updateGridHeight();
            window.addEventListener('resize', updateGridHeight);
            const resizeObserver = new ResizeObserver(updateGridHeight);
            resizeObserver.observe(headerContentRef.current);
            resizeObserver.observe(footerContentRef.current);

            return () => {
                window.removeEventListener('resize', updateGridHeight);
                resizeObserver.disconnect();
            }
        }
    }, [headerContentRef?.current, footerContentRef?.current]);

    const vtRef = useRef();

    const scrollToRow = useCallback((rowIds = [], currentIndex = 0) => {
        const rowIndex = rowIds[currentIndex];
        if (typeof(rowIndex) === "number") {

            vtRef.current.scrollToRow(rowIndex > 0 ? rowIndex - 1 : rowIndex);
            setHighlightRows([rowIndex]);
        } else {
            setHighlightRows([]);
        }
    }, [vtRef]);

    return (
              <div id={"arGrid"} className={classes.arGrid}>
                  <div className={"filters-container"} ref={headerContentRef}>
                      <PlaceholderWrapper commandInstruction={true}/>
                      <ARFilters hiddenColumnsInfo={hiddenColumnsInfo} gridHiddenColumns={gridHiddenColumns} pageProps={pageProps} toggleHiddenColumns={toggleHiddenColumns} onChangeTabNumber={onChangeTabNumber} onChangeFilters={onChangeFilters} renderReport={renderReport}>
                          <ARSearchRow rows={rows} scrollToRow={scrollToRow}/>
                      </ARFilters>
                  </div>
                  <Grid
                    rows={rows.slice(1)}
                    columns={columns}
                  >
                      <FilteringState defaultFilters={[]} />
                      <VirtualTable
                        columnExtensions={columnStyles}
                        cellComponent={(props) => <VirtualCell {...props} fontSize={fontSize}
                            onContextMenu={(e) => contextMenuPropsHandler(e, props.row)} />}
                        height={gridHeight}
                        estimatedRowHeight={22}
                        ref={vtRef}
                      />
                      <ARGridCellExtensions for={columnNames} toggleExpand={toggleExpand} applyChanges={applyChanges}
                                            highlightRows={highlightRows} />

                      <TableHeaderRow
                        cellComponent={(props) => <HeaderCell {...props} fontSize={fontSize} />}
                      />
                      <TableColumnVisibility
                        hiddenColumnNames={gridHiddenColumns}
                      />
                      <TableBandHeader
                        columnBands={columnBands}
                        cellComponent={BandCell}
                      />

                      <TableFilterRow rowHeight={20} cellComponent={(props) => <FilterCellContent {...props} toggleExpand={toggleExpandLevel} firstRow={rows[0]} rowTreeInfo={rowTreeInfo} highlightRows={highlightRows} columnStyles={columnStyles} fontSize={fontSize} />} />

                      <ScreenFormatContext.Consumer>
                          {isDesktop =>
                            isDesktop &&
                            <TableFixedColumns leftColumns={frozenColumnNames} cellComponent={
                                (props) => <FixedCell {...props} fontSize={fontSize}
                                                      onContextMenu={(e) => contextMenuPropsHandler(e, props.row)} />
                            }
                            />

                          }
                      </ScreenFormatContext.Consumer>
                  </Grid>


                  <div className={'d-flex justify-content-between m-2'} ref={footerContentRef}>
                      <Notation />
                      <ARGridFontSize fontSize={fontSize} changeHandler={changeFontSize} />
                      <div/>
                      <div/>
                      <div/>
                      <div/>
                      {/*<div>*/}
                      {/*    {!!pageProps.CanRepForm &&*/}
                      {/*      <button type="button" className={"btn btn-secondary btn-sm rounded-pill"}>Сформировать письма</button>*/}
                      {/*    }*/}
                      {/*</div>*/}
                      <div className={"mb-auto"}>
                          <FixButton isVisible={!!pageProps.CanAgreeForm} onClick={fixYB} />
                      </div>
                  </div>

                  {/* ---- Context Menu and Popup Windows ---- */}
                  {contextMenuProps.isOpen &&
                    <ARGridContextMenu {...contextMenuProps} onClose={contextMenuClose} onOpenDetails={displayDetails} />
                  }
                  <ARDetails rowKey={rowKey} onClose={detailsPopupClose} popupType={popupType} name={name} orgStructId={orgStructId} access={access} pageProps={pageProps} />
              </div>

    );
}

const ARGrid = React.memo(ARGridComponent);

const ARGridDataConnector = ({rootPath = ''}) => {
    const [state, setState] = useState({
        initialized: false,
        hiddenColumnsSubgridKey: null,
        tabNumber: null,
        salaryChangeIntId: null,
        toDenyOpenForm: false,
        messOnDenyOpenForm: null
    });
    const dispatch = useDispatch();

    const [listForBossPO, listForBossPOParams, pagePropsDataItem, ListBPO_Fix, tabNumber] = useSelector(({annualRemunerationReducer, appState}) => {
        return [
            annualRemunerationReducer.SalaryChangeData_ListForBossPO,
            annualRemunerationReducer.SalaryChangeData_ListForBossPOParams,
            annualRemunerationReducer.SalaryChangeData_ListForBossPO_Props,
            annualRemunerationReducer.SalaryChangeData_ListBPO_Fix,
            appState.appParams.tabNum
        ];
    });

    const {subgridItem, subgridItemParams} = useSelector(({subgridRepository, dynState}) => {
        if (!state.hiddenColumnsSubgridKey) return {};
        const repItem = subgridRepository[state.hiddenColumnsSubgridKey];
        if (!repItem || !repItem.subgridDescription) return {};
        return {
            subgridItem: dynState[repItem.subgridDescription.subgridItemPath],
            subgridItemParams : repItem.subgridDescription.subgridItemParamsPath ? dynState[repItem.subgridDescription.subgridItemParamsPath] : null
        };
    });

    const getListForBossPOKey = () => {
        let salaryChangeIntId = undefined;
        let key = {};
        if (listForBossPO.isInitialized) {
            const row = listForBossPOParams.getRow(0);
            salaryChangeIntId = row.SalaryChangeIntId;
            key = row;
        }
        if(!salaryChangeIntId && pagePropsDataItem.isInitialized) {
            const row = pagePropsDataItem.getRow(0);
            salaryChangeIntId = undefined;//row.SalaryChangeIntId;
            key = row;
        }
        return {...key, SalaryChangeIntId: salaryChangeIntId, TestTabNum: state.tabNumber || tabNumber}/*"05113"*/;
    }

    const paramsFieldsInfo = listForBossPOParams.rawData.ResultFieldInfo;
    const filterKey = getListForBossPOKey();

    useEffect(async () => {
        if (!pagePropsDataItem.isInitialized) {
            setState({ ...state, tabNumber });
            await dispatch(pagePropsDataItem.initialize({}, { withParams: true }));
        }
    }, []);

    useEffect(async () => {
        /* receive grid dataTable and settings */
        const toDenyOpenForm = !!pagePropsDataItem.getField(0, 'ToDenyOpenForm');
        const messOnDenyOpenForm = pagePropsDataItem.getField(0, 'MessOnDenyOpenForm');
        if(pagePropsDataItem.isInitialized /*&& !!filterKey.SalaryChangeIntId*/ && !toDenyOpenForm) {
            await initGrid();
        } else if (toDenyOpenForm) {
            setState({ ...state, initialized: true, toDenyOpenForm, messOnDenyOpenForm });
        }
    }, [JSON.stringify(filterKey)]);

    useEffect(async () => {
        /* receive grid filters */
        if (paramsFieldsInfo) {
            await initHiddenColumnsFilter();
        }
        return async () => {
            await unmountHiddenColumnsFilter();
        }
    }, [JSON.stringify(paramsFieldsInfo)]);

    useEffect(async () => {
        if (ListBPO_Fix.isInitialized) {
            await dispatch(ListBPO_Fix.reset());
            if (!ListBPO_Fix.isError) {
                await confirm(i18next.t("ARGrid_4"), {hideCancelButton: true, header: " "});
                await initGrid();
            }
        }
    }, [ListBPO_Fix.isInitialized])

    const initGrid = async () => {
        await dispatch(listForBossPO.execute(filterKey, {withParams: true, treeExpanded: true}));
    }

    const initHiddenColumnsFilter = async () => {
        const fieldName = 'ViewableData';
        const [fieldInfo] = paramsFieldsInfo.filter(f => f.Name === fieldName);
        const path = `${rootPath}_${fieldInfo?.Name || ""}`;
        const key = await dispatch(initSubgrid(listForBossPOParams, fieldName, 0, path, false));
        setState({...state, hiddenColumnsSubgridKey: key, initialized: true});
    }
    const unmountHiddenColumnsFilter = async () => {
        await dispatch(unmountSubgrid(listForBossPOParams, state.hiddenColumnsSubgridKey));
    }

    const pageProps_dataItem = pagePropsDataItem.dataTable();
    const listForBossPO_dataItem = listForBossPO.dataTable();

    const hiddenColumnList = subgridItem && subgridItem.dataTable() || [];

    const hiddenColumnNamesList = useMemo(
      () => hiddenColumnList.map(i => i.ComboSetId),
      [hiddenColumnList]);

    const rowTreeInfo = listForBossPO.rawData.RowTreeInfo;
    const resultFieldInfo = listForBossPO.rawData.ResultFieldInfo;

    const rows = useMemo(
      () => GetRows(listForBossPO_dataItem, rowTreeInfo),
    [listForBossPO_dataItem, rowTreeInfo]);

    const pageProps = useMemo(
      () => GetPageProps(pagePropsDataItem, listForBossPO),
      [listForBossPO_dataItem, pageProps_dataItem]);

    const {bands, columns, columnNamesList, frozenColumns, columnStylesList} =  useMemo(
      () => ParseFieldsInfo(resultFieldInfo, hiddenColumnNamesList, listForBossPO),
      [resultFieldInfo, hiddenColumnNamesList]);

    const toggleExpand = useCallback(async (rowIndex, isExpanded) => {
        await dispatch(listForBossPO.treeOperation(rowIndex, !isExpanded));
    }, []);

    const toggleExpandLevel = useCallback(async (level, isExpanded) => {
        await dispatch(listForBossPO.treeOperationByLevel(level, !isExpanded));
    }, []);

    const onChangeTabNumber = useCallback((number) => {
        number = number || tabNumber;
        if (number != state.tabNumber) {
            setState({ ...state, tabNumber: number });
        }
    }, [state]);

    const onChangeFilters = async () => {
        await dispatch(listForBossPO.execute(filterKey, {withParams: true}));
    };

    const refresh = useCallback(async () => {
        const row = listForBossPOParams.getRow(0);
        const params = listForBossPOParams.getRowKey(row);
        await dispatch(listForBossPO.refresh(params, {withParams: true}));
    }, []);

    const applyChanges = useCallback(async ({ value, fieldName, rowIndex }) => {
        await dispatch(listForBossPO.beginEdit(rowIndex));
        await dispatch(listForBossPO.setField(rowIndex, fieldName, value));
        await dispatch(listForBossPO.save(rowIndex));
        await refresh();
    }, []);

    const fixYB = async () => {
        await confirm(i18next.t("ARGrid_5"));
        const row = listForBossPO.getRow(0);
        const rowParams = listForBossPOParams.getRow(0);
        const key = listForBossPOParams.getRowKey(row);
        await dispatch(ListBPO_Fix.initialize({
            ...rowParams,
            Saved_NameTable: (row?.Saved_NameTable || "")
        }, {newItem: true}));
    }

    const renderReport = async (e, reportParamsItem, build) => {
        e.preventDefault();

        for (let key in filterKey) {
            await dispatch(reportParamsItem.setField(0, key, filterKey[key]));
        }

        await build();
    }

    if (!state.initialized) {
        return <div className={"p-1"}>Loading...</div>
    } else if (state.toDenyOpenForm) {
        return <div className={"p-1"}>{state.messOnDenyOpenForm || ""}</div>
    }

    return (
        <ARGrid columns={columns}
              columnBands={bands}
              columnNames={columnNamesList}
              hiddenColumnsInfo={hiddenColumnList}
              frozenColumnNames={frozenColumns}
              columnStyles={columnStylesList}
              rows={rows}
              toggleExpand={toggleExpand}
              toggleExpandLevel={toggleExpandLevel}
              pageProps={pageProps}
              listForBossPOParams={listForBossPOParams}
              applyChanges={applyChanges}
              fixYB={fixYB}
              tabNumber={tabNumber}
              onChangeTabNumber={onChangeTabNumber}
              rowTreeInfo={rowTreeInfo}
              renderReport={renderReport}
              onChangeFilters={onChangeFilters}
        />
    );
}

export default ARGridDataConnector;